import TextField from "./text/TextField";
import TextAreaField from "./text/TextAreaField";
import ReadOnlyTextField from "./text/ReadOnlyTextField";
import HeaderField from "./layout/HeaderField";
import HelpField from "./layout/HelpField";
import HTMLField from "./layout/HTMLField";
import InstructionHTMLField from "./layout/InstructionHTMLField";
import ImageField from "./layout/ImageField";
import LabelField from "./layout/LabelField";
import RadioField from "./choices/RadioField";
import CheckboxField from "./choices/CheckboxField";
import LookupField from "./choices/LookupField";
import SmileyField from "./choices/SmileyField";
import RatingField from "./choices/RatingField";
import VideoField from "./media/VideoField";
import FileField from "./media/FileField";
import PhotoField from "./media/PhotoField";
import DrawingField from "./media/DrawingField";
import PinField from "./media/PinField";
import EmailField from "./personalInfo/EmailField";
import PhoneField from "./personalInfo/PhoneField";
import SignatureField from "./personalInfo/SignatureField";
import DateField from "./dateTime/DateField";
import TimeField from "./dateTime/TimeField";
import DateTimeField from "./dateTime/DateTimeField";
import TimeDifferenceField from "./dateTime/TimeDifferenceField";
import StopwatchField from "./dateTime/StopwatchField";
import SearchField from "./searchItems/SearchField";
import CatalogueField from "./searchItems/CatalogueField";
import BarcodeField from "./searchItems/BarcodeField";
import IBANField from "./searchItems/IBANField";
import LicensePlateField from "./searchItems/LicensePlateField";
import NumberField from "./number/NumberField";
import SliderField from "./number/SliderField";
import CalculationField from "./number/CalculationField";
import PriceField from "./number/PriceField";
import SubformField from "./subform/SubformField";
import LocationField from "./location/LocationField";
import CurrentLocationField from "./location/CurrentLocationField";
import ZipCodeField from "./location/ZipCodeField";
import type { Category, FieldType, WidgetUid } from "../types";
import UnknownField from "./UnknownField";
import type { BaseField, FieldConfig, FieldDataNameProperties, FieldLabelTextProperties } from "./types";

export const categories = {
  layout: [HeaderField, ImageField, HelpField, LabelField, HTMLField, InstructionHTMLField],
  textFields: [TextField, TextAreaField, ReadOnlyTextField],
  choices: [RadioField, CheckboxField, LookupField, SmileyField, RatingField],
  media: [VideoField, FileField, PhotoField, DrawingField, PinField],
  personalInfo: [EmailField, PhoneField, SignatureField],
  dateTime: [DateField, TimeField, DateTimeField, TimeDifferenceField, StopwatchField],
  location: [LocationField, CurrentLocationField, ZipCodeField],
  searchItems: [SearchField, CatalogueField, BarcodeField, IBANField, LicensePlateField],
  number: [NumberField, SliderField, CalculationField, PriceField],
  subform: [SubformField],
} as Record<Category, BaseField<unknown, unknown>[]>;

export const fields = Object.values(categories)
  .flatMap((i) => i)
  .reduce((acc, curr) => ({ ...acc, [curr.type]: curr }), { unknown: UnknownField } as Record<
    FieldType,
    BaseField<unknown, unknown>
  >);

export const removeWidgetVersionNumber = (widget: string): string => widget.substring(0, widget.lastIndexOf(":"));

const fieldTypeToFieldMap = new Map<string, BaseField<unknown, unknown>>();
const widgetUidToFieldMap = new Map<string, BaseField<unknown, unknown>>();
Object.values(fields).forEach((field) => {
  fieldTypeToFieldMap.set(field.type, field);
  widgetUidToFieldMap.set(removeWidgetVersionNumber(field.widget), field);
});

export const widgetUidToField = (widget: WidgetUid): BaseField<unknown, unknown> =>
  widgetUidToFieldMap.get(removeWidgetVersionNumber(widget)) ?? (UnknownField as BaseField<unknown, unknown>);

export const toField = (type: FieldType): BaseField<unknown, unknown> =>
  fieldTypeToFieldMap.get(type) ?? (UnknownField as BaseField<unknown, unknown>);

export const hasData = (
  fieldConfig: FieldConfig,
): fieldConfig is FieldConfig<Required<FieldDataNameProperties & FieldLabelTextProperties>> =>
  toField(fieldConfig.type).hasData;
